import type { SxProps, Theme } from "@mui/material";
import { Autocomplete, TextField } from "@mui/material";
import { useStands } from "../hooks/useStands";

interface StandSelectorProps {
	value: number | null;
	onChange: (value: number | null) => void;
	/** Adds StrtT and StrtB, for the pages that work with straightener rolls. */
	includeStraighteners?: boolean;
	/** Adds an "All" option and takes away the clear button: a filter always has a value. */
	includeAll?: boolean;
	label?: string;
	fullWidth?: boolean;
	size?: "small" | "medium";
	required?: boolean;
	error?: boolean;
	helperText?: string;
	disabled?: boolean;
	/** Keeps the label above the field while empty; turn off next to fields that keep it inside. */
	shrinkLabel?: boolean;
	sx?: SxProps<Theme>;
}

type Option = { Id: number; Name: string };

const ALL_OPTION: Option = { Id: 0, Name: "All" };

export const StandSelector = ({
	value,
	onChange,
	includeStraighteners = false,
	includeAll = false,
	label = "Stand",
	fullWidth = false,
	size = "small",
	required = false,
	error = false,
	helperText,
	disabled = false,
	shrinkLabel = true,
	sx,
}: StandSelectorProps) => {
	const { data: stands = [], isLoading } = useStands({ includeStraighteners });
	const options: Option[] = includeAll ? [ALL_OPTION, ...stands] : stands;
	const selected =
		options.find((option) => option.Id === value) ??
		(includeAll ? ALL_OPTION : null);

	return (
		<Autocomplete
			options={options}
			value={selected}
			size={size}
			fullWidth={fullWidth}
			disabled={disabled || isLoading}
			disableClearable={includeAll || required}
			autoHighlight
			getOptionLabel={(option) => option.Name}
			isOptionEqualToValue={(option, current) => option.Id === current.Id}
			onChange={(_event, option) =>
				onChange(!option || option.Id === ALL_OPTION.Id ? null : option.Id)
			}
			renderOption={(props, option) => {
				const { key, ...rest } = props as typeof props & { key: string };
				return (
					<li key={option.Id} {...rest}>
						{option.Name}
					</li>
				);
			}}
			// The list opens as wide as its longest option instead of wrapping it.
			slotProps={{ popper: { sx: { minWidth: "fit-content" } } }}
			sx={{ minWidth: 120, ...sx } as SxProps<Theme>}
			renderInput={(params) => (
				<TextField
					{...params}
					label={label}
					required={required}
					error={error}
					helperText={helperText}
					slotProps={shrinkLabel ? { inputLabel: { shrink: true } } : undefined}
				/>
			)}
		/>
	);
};
